// A bare board for the classifier: pieces read from a FEN, the squares each
// piece attacks, a move played from UCI, and the move written in SAN. Only
// what the patterns need: no legality check beyond attacks, so a pinned
// piece still counts as attacking, as a player sees it at a glance.
import type { Color } from '@chess/api-model';

export type PieceKind = 'pawn' | 'knight' | 'bishop' | 'rook' | 'queen' | 'king';
export interface Piece {
  kind: PieceKind;
  colour: Color;
}
export type Square = string;

/** Material, in pawns; the king is beyond price. */
export const VALUE: Record<PieceKind, number> = { pawn: 1, knight: 3, bishop: 3, rook: 5, queen: 9, king: 100 };
const KIND: Record<string, PieceKind> = { p: 'pawn', n: 'knight', b: 'bishop', r: 'rook', q: 'queen', k: 'king' };
const LETTER: Record<PieceKind, string> = { pawn: '', knight: 'N', bishop: 'B', rook: 'R', queen: 'Q', king: 'K' };
const FILES = 'abcdefgh';

const at = (file: number, rank: number): Square | undefined => (file >= 0 && file < 8 && rank >= 0 && rank < 8 ? `${FILES[file]}${rank + 1}` : undefined);
const fileOf = (sq: Square) => FILES.indexOf(sq[0]);
const rankOf = (sq: Square) => Number(sq[1]) - 1;
export const opposite = (c: Color): Color => (c === 'white' ? 'black' : 'white');

const KNIGHT = [[1, 2], [2, 1], [2, -1], [1, -2], [-1, -2], [-2, -1], [-2, 1], [-1, 2]];
const KING = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];
const ROOK = [[1, 0], [-1, 0], [0, 1], [0, -1]];
const BISHOP = [[1, 1], [1, -1], [-1, 1], [-1, -1]];

export class Board {
  private readonly squares: Map<Square, Piece>;

  private constructor(squares: Map<Square, Piece>) {
    this.squares = squares;
  }

  /** The pieces of a FEN's placement field; the rest of the FEN is ignored. */
  static fromFen(fen: string): Board {
    const squares = new Map<Square, Piece>();
    fen
      .split(' ')[0]
      .split('/')
      .forEach((row, r) => {
        let file = 0;
        for (const ch of row) {
          const empty = Number(ch);
          if (Number.isInteger(empty)) {
            file += empty;
            continue;
          }
          squares.set(`${FILES[file]}${8 - r}`, { kind: KIND[ch.toLowerCase()], colour: ch === ch.toUpperCase() ? 'white' : 'black' });
          file += 1;
        }
      });
    return new Board(squares);
  }

  get(sq: Square): Piece | undefined {
    return this.squares.get(sq);
  }

  /** Every piece on the board, with its square. */
  pieces(): Array<{ square: Square } & Piece> {
    return [...this.squares].map(([square, p]) => ({ square, ...p }));
  }

  /** The squares the piece on `from` attacks (for a pawn, its two diagonals). */
  attacksFrom(from: Square): Square[] {
    const piece = this.get(from);
    if (!piece) return [];
    const f = fileOf(from);
    const r = rankOf(from);
    const steps = (deltas: number[][]) => deltas.map(([df, dr]) => at(f + df, r + dr)).filter((s): s is Square => !!s);
    const slide = (deltas: number[][]) => {
      const out: Square[] = [];
      for (const [df, dr] of deltas) {
        for (let i = 1; ; i++) {
          const sq = at(f + df * i, r + dr * i);
          if (!sq) break;
          out.push(sq);
          if (this.get(sq)) break;
        }
      }
      return out;
    };
    switch (piece.kind) {
      case 'pawn': {
        const dr = piece.colour === 'white' ? 1 : -1;
        return steps([[1, dr], [-1, dr]]);
      }
      case 'knight':
        return steps(KNIGHT);
      case 'king':
        return steps(KING);
      case 'bishop':
        return slide(BISHOP);
      case 'rook':
        return slide(ROOK);
      case 'queen':
        return slide([...ROOK, ...BISHOP]);
    }
  }

  /** The squares of `colour`'s pieces that attack `target`. */
  attackers(target: Square, colour: Color): Square[] {
    return this.pieces()
      .filter((p) => p.colour === colour && this.attacksFrom(p.square).includes(target))
      .map((p) => p.square);
  }

  /** Whether `colour`'s king stands attacked. */
  inCheck(colour: Color): boolean {
    const king = this.pieces().find((p) => p.kind === 'king' && p.colour === colour);
    return !!king && this.attackers(king.square, opposite(colour)).length > 0;
  }

  /** What the move takes, if anything: the piece on its target, or the pawn passed by en passant. */
  captured(uci: string): ({ square: Square } & Piece) | undefined {
    const from = uci.slice(0, 2);
    const to = uci.slice(2, 4);
    const mover = this.get(from);
    const target = this.get(to);
    if (target) return { square: to, ...target };
    if (mover?.kind === 'pawn' && fileOf(from) !== fileOf(to)) {
      const passed = at(fileOf(to), rankOf(from));
      const pawn = passed ? this.get(passed) : undefined;
      if (passed && pawn) return { square: passed, ...pawn };
    }
    return undefined;
  }

  /** The board after the move: captures, en passant, castling's rook and promotion included. */
  play(uci: string): Board {
    const from = uci.slice(0, 2);
    const to = uci.slice(2, 4);
    const next = new Map(this.squares);
    const mover = next.get(from);
    if (!mover) return new Board(next);
    const taken = this.captured(uci);
    if (taken) next.delete(taken.square);
    next.delete(from);
    const promotion = uci[4] ? KIND[uci[4]] : undefined;
    next.set(to, promotion ? { kind: promotion, colour: mover.colour } : mover);
    if (mover.kind === 'king' && Math.abs(fileOf(to) - fileOf(from)) === 2) {
      const rank = rankOf(from);
      const [rookFrom, rookTo] = fileOf(to) > fileOf(from) ? [at(7, rank)!, at(5, rank)!] : [at(0, rank)!, at(3, rank)!];
      const rook = next.get(rookFrom);
      if (rook) {
        next.delete(rookFrom);
        next.set(rookTo, rook);
      }
    }
    return new Board(next);
  }

  /**
   * The move in SAN. `mate` marks a move the engine scores as mate in one
   * (`#`); otherwise a check is `+`. Disambiguation goes by attacks, so a
   * pinned twin still counts, as it does for most readers.
   */
  san(uci: string, mate = false): string {
    const from = uci.slice(0, 2);
    const to = uci.slice(2, 4);
    const piece = this.get(from);
    if (!piece) return uci;
    const after = this.play(uci);
    const suffix = mate ? '#' : after.inCheck(opposite(piece.colour)) ? '+' : '';
    if (piece.kind === 'king' && Math.abs(fileOf(to) - fileOf(from)) === 2) return `${fileOf(to) > fileOf(from) ? 'O-O' : 'O-O-O'}${suffix}`;
    const takes = this.captured(uci) ? 'x' : '';
    if (piece.kind === 'pawn') {
      const promotion = uci[4] ? `=${LETTER[KIND[uci[4]]]}` : '';
      return `${takes ? `${from[0]}x` : ''}${to}${promotion}${suffix}`;
    }
    const twins = this.pieces().filter((p) => p.square !== from && p.kind === piece.kind && p.colour === piece.colour && this.attacksFrom(p.square).includes(to));
    let which = '';
    if (twins.length) {
      if (!twins.some((t) => t.square[0] === from[0])) which = from[0];
      else if (!twins.some((t) => t.square[1] === from[1])) which = from[1];
      else which = from;
    }
    return `${LETTER[piece.kind]}${which}${takes}${to}${suffix}`;
  }
}
